<template>
    <div>
        <div class="mb-3">
            <span class="item-detail-title">{{ $t('credit_note.original_invoice') }}</span>
            {{ $t('common.id') }}: {{ invoice.id }}
            <span v-if="invoice.contract"> – {{ invoice.contract.name }}</span>
        </div>

        <div class="mb-3">
            <span class="item-detail-title">{{ $t('credit_note.info') }}</span>
            <textarea rows="3" maxlength="500" class="form-control form-control-sm" v-model="info" :placeholder="$t('credit_note.info_tip')"/>
        </div>

        <div class="mb-3 d-flex justify-content-between align-items-start">
            <span class="item-detail-title">{{ $t('contracts.attachments') }}</span>
            <span v-if="contractAttachments.length === 0" class="text-muted small">{{ $t('daily_summary.no_contract_attachments') }}</span>
            <invoice-attachment-selector
                v-else
                v-model="selectedAttachments"
                :contract-attachments="contractAttachments"
                :contract-id="invoice.contract.id"
                @downloadAttachment="downloadAttachment"
            />
        </div>

        <div v-if="rows.length === 0" class="text-center text-muted py-4">
            {{ $t('credit_note.no_rows') }}
        </div>

        <div v-if="rows.length > 0" class="table-responsive">
            <table class="table table-sm" style="font-size: .85em">
                <thead>
                    <tr>
                        <th>{{ $t('credit_note.row_id') }}</th>
                        <th>{{ $t('orders.order') }}</th>
                        <th>{{ $t('common.time_range') }}</th>
                        <th class="text-end">{{ $t('trip_list.unit_price') }}</th>
                        <th>{{ $t('material_reports.unit') }}</th>
                        <th>{{ $t('account.account') }}</th>
                        <th>{{ $t('account.invoicing_code') }}</th>
                        <th class="text-end">{{ $t('credit_note.original_sum') }}</th>
                        <th class="text-end">{{ $t('credit_note.already_credited') }}</th>
                        <th class="text-end">{{ $t('credit_note.credit_sum') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in rows" :key="index">
                        <td>{{ row.invoice_row_id }}</td>
                        <td>{{ getInvoiceRowOrder(row.invoice_row_id, invoice) }}</td>
                        <td>{{ getInvoiceRowDates(row.invoice_row_id, invoice) }}</td>
                        <td class="text-end">{{ formatCurrency(getInvoiceRowUnitPrice(row.invoice_row_id, invoice) / 100) }}</td>
                        <td>{{ getInvoiceRowUnit(row.invoice_row_id, invoice) }}</td>
                        <td>{{ getInvoiceRowAccount(row.invoice_row_id, invoice) }}</td>
                        <td>{{ getInvoiceRowInvoicingCode(row.invoice_row_id, invoice) }}</td>
                        <td class="text-end">{{ formatCurrency(row.original_cost) }}</td>
                        <td class="text-end">
                            <span v-if="row.already_credited !== 0" class="text-danger">{{ formatCurrency(row.already_credited) }}</span>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-end ps-2" style="min-width: 100px;">
                            <input
                                type="number"
                                class="form-control form-control-sm text-end"
                                :max="-0.01"
                                :min="row.min_value"
                                step="0.01"
                                v-model.number="row.credit_cost"
                                @blur="clampValue(index)"
                            />
                        </td>
                        <td class="text-center">
                            <button class="btn btn-sm btn-outline-danger" @click="removeRow(index)">
                                <i class="fas fa-trash"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr class="fw-bold">
                        <td colspan="7" class="text-bold">{{ $t('credit_note.total') }}</td>
                        <td class="text-end text-bold">{{ formatCurrency(totalOriginal) }}</td>
                        <td class="text-end text-bold"></td>
                        <td class="text-end text-bold">{{ formatCurrency(totalCreditSum) }}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <div class="button-container d-flex justify-content-end mt-3">
            <button class="btn btn-secondary btn-sm me-2" @click="$emit('close')">
                {{ $t('common.cancel') }}
            </button>
            <span v-if="!hasContractSystem(user)" class="text-danger small me-2 align-self-center">{{ $t('invoice_history.invoicing_integration_required') }}</span>
            <button
                class="btn btn-success btn-sm"
                :disabled="rows.length === 0 || submitting || !hasContractSystem(user)"
                @click="submit"
            >
                <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('credit_note.submit') }}
            </button>
        </div>
    </div>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin'
import { inject } from 'vue'
import {priceHelper} from "../mixins/PricingMixin.js";
import {invoicingHelper} from "../mixins/InvoicingMixin.js";
import {attachmentHelper} from "../mixins/AttachmentMixin.js";
import {downloadHelper} from '../mixins/DownloadMixin'
import {userHelper} from '../mixins/UserMixin'
import InvoiceAttachmentSelector from './InvoiceAttachmentSelector.vue';

export default {
    name: 'CreditNoteEditor',
    components: { InvoiceAttachmentSelector },
    mixins: [restApi, priceHelper, invoicingHelper, attachmentHelper, downloadHelper, userHelper],
    props: {
        invoice: { type: Object, required: true },
        user: { type: Object, default: null }
    },
    emits: ['close', 'submitted'],
    setup() {
        const showAlert = inject('showAlert')
        return { showAlert }
    },
    data() {
        return {
            rows: [],
            info: '',
            submitting: false,
            loading: false,
            contractAttachments: [],
            selectedAttachments: []
        }
    },
    computed: {
        totalCreditSum() {
            return this.rows.reduce((sum, r) => sum + (r.credit_cost || 0), 0)
        },
        totalOriginal() {
            return this.rows.reduce((sum, r) => sum + (r.original_cost || 0), 0)
        }
    },
    mounted() {
        this.initRows()
        this.fetchContractAttachments()
    },
    methods: {
        fetchContractAttachments() {
            this.contractAttachments = this.invoice.contract?.attachments || []
        },

        downloadAttachment(attachment) {
            this.loading = true
            this.restFetch(
                this.contractAttachmentUrl + this.invoice.contract.id + '/' + attachment.id,
                response => {
                    const fileName = attachment.file_original_name || attachment.filename || 'download'
                    this.saveFile(fileName, response.data)
                    this.loading = false
                }, () => {
                    this.loading = false
                })
        },

        initRows() {
            const invoiceRows = this.invoice.invoice_rows || []
            const existingCreditByRowId = {};(this.invoice.credit_notes || []).filter(cn => cn.status !== 4).forEach(cn => {
                (cn.credit_note_rows || []).forEach(r => {
                    existingCreditByRowId[r.invoice_row_id] =
                        (existingCreditByRowId[r.invoice_row_id] || 0) + (r.sum || 0)
                })
            })
            this.rows = []
            invoiceRows.forEach(ir => {
                    const rowSum = ir.items.reduce((sum, item) => sum + (item.invoiceable_sum ?? 0), 0)
                    const alreadyCredited = (existingCreditByRowId[ir.id] || 0) / 100
                    const remaining = -(rowSum) - alreadyCredited
                    if (remaining >= -0.01) return
                    this.rows.push({
                        invoice_row_id: ir.id,
                        original_cost: rowSum,
                        already_credited: alreadyCredited,
                        credit_cost: Math.round(remaining * 100) / 100,
                        min_value: Math.round(remaining * 100) / 100,
                })
            })
        },

        clampValue(index) {
            const row = this.rows[index]
            if (row.credit_cost > -0.01) {
                row.credit_cost = -0.01
            }
            if (row.credit_cost < row.min_value) {
                row.credit_cost = row.min_value
            }
            row.credit_cost = Math.round(row.credit_cost * 100) / 100
        },

        removeRow(index) {
            this.rows.splice(index, 1)
        },

        submit() {
            if (!this.hasContractSystem(this.user)) {
                if (this.showAlert) {
                    this.showAlert(this.$t('invoice_history.invoicing_integration_required'))
                }
                return
            }
            this.submitting = true
            const payload = {
                invoice_id: this.invoice.id,
                info: this.info,
                invoice_rows: this.rows.map(r => ({
                    invoice_row_id: r.invoice_row_id,
                    sum: Math.round(r.credit_cost * 100),
                    info: r.info || ''
                })),
                attachment_ids: this.selectedAttachments
            }
            this.restAdd(
                this.creditNoteUrl,
                payload,
                this.handleSuccess,
                this.handleError
            )
        },
        handleSuccess() {
            this.submitting = false
            if (this.showAlert) {
                this.showAlert(this.$t('credit_note.success'), 'success')
            }
            this.$emit('submitted')
        },
        handleError() {
            this.submitting = false
            if (this.showAlert) {
                this.showAlert(this.$t('credit_note.error'))
            }
        }
    }
}
</script>
